import React, {useEffect, useMemo, useState} from 'react';
import {search} from '../../libraries/DnaApi';

const FALLBACK_COVER = '/images/cover-placeholder.jpg';

/**
 * The dedicated results page: albums that match up top for browsing, then a
 * deep song list. Rows carry title, artist, album and year only.
 */
export default function SearchResultsView({q, onPlay, onAlbum, onBack}) {
    const [hits, setHits] = useState(null);

    useEffect(() => {
        setHits(null);
        let active = true;
        search(encodeURIComponent(q) + '&limit=50')
            .then((res) => active && setHits(res?.hits || []))
            .catch(() => active && setHits([]));
        return () => {
            active = false;
        };
    }, [q]);

    // Albums surface from the hits themselves (album names are searchable,
    // so querying an album title brings its songs along).
    const albums = useMemo(() => {
        const byId = new Map();
        (hits || []).forEach((h) => {
            if (!h.album_apple_id || !h.album) {
                return;
            }
            const a = byId.get(h.album_apple_id);
            if (a) {
                a.count++;
            } else {
                byId.set(h.album_apple_id, {
                    id: h.album_apple_id, album: h.album, artist: h.artist,
                    year: h.year, cover: h.cover, count: 1,
                });
            }
        });
        return [...byId.values()].sort((a, b) => b.count - a.count).slice(0, 10);
    }, [hits]);

    return (
        <div className="mx-auto w-full max-w-[1050px]">
            <button type="button" onClick={onBack}
                    className="mb-5 flex items-center gap-2 text-[13px] font-semibold text-white/50 transition hover:text-white">
                <i className="fa-solid fa-arrow-left text-[11px]"/> Back
            </button>
            <h1 className="font-heading text-[26px] font-bold tracking-[-0.015em] sm:text-[30px]">
                Results for “{q}”
            </h1>

            {hits === null && <p className="mt-6 text-sm text-white/40">Searching…</p>}
            {hits !== null && hits.length === 0 && (
                <p className="mt-6 text-sm text-white/40">Nothing matches. Try fewer words.</p>
            )}

            {albums.length > 0 && (
                <div className="mt-7">
                    <h3 className="text-[14px] font-bold">Albums</h3>
                    <div className="scrollbar-subtle -mx-1 mt-3 flex snap-x gap-4 overflow-x-auto px-1 pb-2">
                        {albums.map((a) => (
                            <button key={a.id} type="button" onClick={() => onAlbum(a.id)}
                                    className="group w-[128px] flex-none snap-start text-left">
                                <img src={a.cover || FALLBACK_COVER} alt=""
                                     className="aspect-square w-full rounded-xl object-cover shadow-[0_16px_34px_rgba(0,0,0,0.45)] transition group-hover:scale-[1.03]"/>
                                <span className="mt-2 block truncate text-[12px] font-semibold">{a.album}</span>
                                <span className="block truncate text-[10.5px] text-white/40">
                                    {[a.artist, a.year].filter(Boolean).join(' · ')}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {hits !== null && hits.length > 0 && (
                <div className="mt-7">
                    <h3 className="text-[14px] font-bold">Songs</h3>
                    <div className="mt-2">
                        {hits.map((h) => (
                            <button key={h.id} type="button" onClick={() => onPlay(h, [h])}
                                    className="flex w-full items-center gap-3.5 rounded-2xl p-2.5 text-left transition hover:bg-white/[0.07]">
                                <img src={h.cover || FALLBACK_COVER} alt="" loading="lazy"
                                     className="h-11 w-11 flex-none rounded-xl object-cover"/>
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-2 text-[14px] font-semibold">
                                        <span className="truncate">{h.title}</span>
                                        {h.is_live && <span className="flex-none rounded-full bg-apple-red/15 px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase text-apple-red">Live</span>}
                                        {h.is_remix && <span className="flex-none rounded-full bg-white/10 px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase text-white/60">Remix</span>}
                                    </span>
                                    <span className="block truncate text-[12px] text-white/[0.45]">
                                        {[h.artist, h.album, h.year].filter(Boolean).join(' · ')}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
